import type { App } from 'vue'

type DiagnosticLevel = 'info' | 'warn' | 'error'
type DiagnosticCategory =
  | 'app'
  | 'request'
  | 'response'
  | 'vue'
  | 'window'
  | 'promise'
type RequestOutcome =
  | 'success'
  | 'cancelled'
  | 'business-error'
  | 'http-error'
  | 'request-error'

interface DiagnosticEntry {
  id: number
  timestamp: string
  level: DiagnosticLevel
  category: DiagnosticCategory
  event: string
  route: string
  appVersion: string
  details?: Record<string, unknown>
}

interface PendingRequest {
  requestId: string
  method: string
  url: string
  route: string
  startedAt: string
  expectsPage: boolean
}

interface DiagnosticRuntime {
  installed: boolean
  entries: DiagnosticEntry[]
  pendingRequests: Record<string, PendingRequest>
}

export interface RequestDiagnosticContext extends PendingRequest {
  startedAtMs: number
  finished: boolean
  slowTimer?: number
}

export interface ResponseShapeSummary {
  shape: string
  empty: boolean
  pageLike: boolean
  itemCount?: number
  hasTotal?: boolean
}

declare global {
  interface Window {
    __AUXILIARY_DIAGNOSTICS__?: DiagnosticRuntime
    getAuxiliaryDiagnostics?: () => DiagnosticEntry[]
  }
}

const APP_VERSION = process.env.VUE_APP_VERSION || '1.3.7'
const MAX_ENTRIES = 300
const SLOW_REQUEST_MS = 10_000

let entrySequence = 0
let requestSequence = 0

const runtime: DiagnosticRuntime = window.__AUXILIARY_DIAGNOSTICS__ || {
  installed: false,
  entries: [],
  pendingRequests: {}
}

window.__AUXILIARY_DIAGNOSTICS__ = runtime
window.getAuxiliaryDiagnostics = () =>
  runtime.entries.map((entry) => ({ ...entry }))

function getCurrentRoute() {
  return (window.location.hash || '/').split('?')[0]
}

function getSafeUrl(url?: string) {
  if (!url) return 'unknown'
  return url.split('?')[0].split('#')[0]
}

function getDuration(startedAtMs: number) {
  return Math.max(0, Math.round(performance.now() - startedAtMs))
}

function writeConsole(entry: DiagnosticEntry) {
  const message = `[Diagnostics][${entry.category}] ${entry.event}`
  if (entry.level === 'error') {
    console.error(message, entry)
  } else if (entry.level === 'warn') {
    console.warn(message, entry)
  } else {
    console.debug(message, entry)
  }
}

export function reportDiagnostic(
  level: DiagnosticLevel,
  category: DiagnosticCategory,
  event: string,
  details?: Record<string, unknown>
) {
  try {
    const entry: DiagnosticEntry = {
      id: ++entrySequence,
      timestamp: new Date().toISOString(),
      level,
      category,
      event,
      route: getCurrentRoute(),
      appVersion: APP_VERSION,
      details
    }

    runtime.entries.push(entry)
    if (runtime.entries.length > MAX_ENTRIES) {
      runtime.entries.splice(0, runtime.entries.length - MAX_ENTRIES)
    }
    writeConsole(entry)
  } catch (error) {
    console.warn('[Diagnostics] 记录诊断信息失败', error)
  }
}

function classifyError(error: unknown) {
  const message =
    error instanceof Error ? error.message : String(error || 'Unknown error')

  if (
    /maximum recursive updates|maximum call stack size exceeded/i.test(message)
  ) {
    return 'recursive-update'
  }
  if (/timeout|ECONNABORTED/i.test(message)) {
    return 'timeout'
  }
  if (/network error|failed to fetch/i.test(message)) {
    return 'network'
  }
  return 'runtime'
}

function summarizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      errorType: classifyError(error),
      name: error.name,
      message: error.message,
      stack: error.stack?.slice(0, 4000)
    }
  }

  return {
    errorType: classifyError(error),
    name: typeof error,
    message: typeof error === 'string' ? error : 'Non-Error rejection'
  }
}

export function installGlobalDiagnostics(app: App) {
  if (runtime.installed) return
  runtime.installed = true

  const previousErrorHandler = app.config.errorHandler
  app.config.errorHandler = (error, instance, info) => {
    reportDiagnostic('error', 'vue', 'vue-error', {
      ...summarizeError(error),
      component:
        (instance as any)?.$options?.name ||
        (instance as any)?.$options?.__name ||
        'anonymous',
      info
    })
    previousErrorHandler?.(error, instance, info)
  }

  window.addEventListener('error', (event) => {
    reportDiagnostic('error', 'window', 'window-error', {
      ...summarizeError(event.error || event.message),
      filename: event.filename,
      line: event.lineno,
      column: event.colno
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportDiagnostic('error', 'promise', 'unhandled-rejection', {
      ...summarizeError(event.reason)
    })
  })

  reportDiagnostic('info', 'app', 'diagnostics-installed', {
    environment: process.env.NODE_ENV,
    slowRequestThresholdMs: SLOW_REQUEST_MS
  })
}

export function startRequestDiagnostic(input: {
  method?: string
  url?: string
  expectsPage?: boolean
}) {
  const startedAtMs = performance.now()
  const context: RequestDiagnosticContext = {
    requestId: `req-${Date.now().toString(36)}-${++requestSequence}`,
    method: (input.method || 'GET').toUpperCase(),
    url: getSafeUrl(input.url),
    route: getCurrentRoute(),
    startedAt: new Date().toISOString(),
    startedAtMs,
    expectsPage: Boolean(input.expectsPage),
    finished: false
  }

  runtime.pendingRequests[context.requestId] = {
    requestId: context.requestId,
    method: context.method,
    url: context.url,
    route: context.route,
    startedAt: context.startedAt,
    expectsPage: context.expectsPage
  }

  context.slowTimer = window.setTimeout(() => {
    if (context.finished) return
    reportDiagnostic('warn', 'request', 'request-pending', {
      requestId: context.requestId,
      method: context.method,
      url: context.url,
      requestRoute: context.route,
      durationMs: getDuration(startedAtMs)
    })
  }, SLOW_REQUEST_MS)

  reportDiagnostic('info', 'request', 'request-start', {
    requestId: context.requestId,
    method: context.method,
    url: context.url,
    requestRoute: context.route,
    expectsPage: context.expectsPage
  })

  return context
}

export function finishRequestDiagnostic(
  context: RequestDiagnosticContext | undefined,
  outcome: RequestOutcome,
  details?: Record<string, unknown>
) {
  if (!context || context.finished) return
  context.finished = true
  if (context.slowTimer !== undefined) {
    window.clearTimeout(context.slowTimer)
  }
  delete runtime.pendingRequests[context.requestId]

  const level: DiagnosticLevel =
    outcome === 'success' || outcome === 'cancelled' ? 'info' : 'error'
  reportDiagnostic(level, 'request', `request-${outcome}`, {
    requestId: context.requestId,
    method: context.method,
    url: context.url,
    requestRoute: context.route,
    durationMs: getDuration(context.startedAtMs),
    ...details
  })
}

export function reportRequestFailure(
  context: RequestDiagnosticContext | undefined,
  error: any,
  outcome: 'http-error' | 'request-error' = 'request-error'
) {
  finishRequestDiagnostic(context, outcome, {
    ...summarizeError(error),
    httpStatus: error?.response?.status,
    axiosCode: error?.code
  })
}

export function summarizeResponseShape(payload: unknown): ResponseShapeSummary {
  if (payload instanceof Blob) {
    return { shape: 'blob', empty: payload.size === 0, pageLike: false }
  }
  if (payload === null || payload === undefined) {
    return { shape: 'null', empty: true, pageLike: false }
  }
  if (Array.isArray(payload)) {
    return {
      shape: 'array',
      empty: payload.length === 0,
      pageLike: false,
      itemCount: payload.length
    }
  }
  if (typeof payload !== 'object') {
    return {
      shape: typeof payload,
      empty: payload === '',
      pageLike: false
    }
  }

  const value = payload as Record<string, unknown>
  const list = Array.isArray(value.list)
    ? value.list
    : Array.isArray(value.records)
    ? value.records
    : undefined

  if (list) {
    return {
      shape: Array.isArray(value.list) ? 'page-list' : 'page-records',
      empty: list.length === 0,
      pageLike: true,
      itemCount: list.length,
      hasTotal: Number.isFinite(Number(value.total))
    }
  }

  return {
    shape: 'object',
    empty: Object.keys(value).length === 0,
    pageLike: false,
    hasTotal: Object.prototype.hasOwnProperty.call(value, 'total')
  }
}

export function reportResponseStructureSuspect(
  context: RequestDiagnosticContext | undefined,
  summary: ResponseShapeSummary
) {
  if (
    !context ||
    !context.expectsPage ||
    summary.pageLike ||
    summary.shape === 'blob'
  )
    return

  reportDiagnostic('warn', 'response', 'response-structure-suspect', {
    requestId: context.requestId,
    method: context.method,
    url: context.url,
    requestRoute: context.route,
    expected: 'page-list-or-page-records',
    actual: summary.shape,
    empty: summary.empty,
    hasTotal: summary.hasTotal
  })
}
